<template>
  <div class="tabs-snippet">
    <div
      class="tabs-snippet__tabslist-container"
      :class="classes.tabslistContainer"
    >
      <div
        ref="tabslist"
        :aria-labelledby="headingId"
        class="tabs-snippet__tabslist"
        :class="classes.tabslist"
        role="tablist"
      >
        <btn
          v-for="(tab, index) of tabs"
          :id="getTabId(index)"
          :key="tab.id"
          :ref="tab.id"
          :aria-controls="getPanelId(index)"
          :aria-selected="isActiveTab(tab.id)"
          class="tabs-snippet__trigger"
          :class="[
            classes.tab,
            { [$class('active')]: isActiveTab(tab.id) },
          ]"
          :class-label="classes.tabLabel"
          :label="tab.title"
          role="tab"
          :modifiers="btnModifiers"
          :tabindex="getTabTabindex(tab.id)"
          type="button"
          @click="setSelectedTab(index)"
          @keydown="handleTabKeydown($event, index)"
        />
      </div>
    </div>

    <template v-if="content">
      <template
        v-for="(tab, index) of tabs"
        :key="`tab-${index}`"
      >
        <div
          v-show="isActiveTab(tab.id)"
          :id="getPanelId(index)"
          :aria-labelledby="getTabId(index)"
          class="tabs-snippet__panel"
          :class="classes.tabspanel"
          role="tabpanel"
        >
          <slot
            :name="dynamic ? tab.id : 'tabpanel'"
            v-bind="tab"
          />
        </div>
      </template>
    </template>
  </div>
</template>

<script>

/**
 * Vue: Tabs snippet (tabs-snippet)
 * -----------------------------------------------------------------------------
 * Render a series of tabs with corresponding tabpanels.
 * - Tabpanel content is slotted and referenced by properties in the
 *   corersponding array element name.
 * - Follows Tabs Pattern - https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 *
 * @example
 * <tabs-snippet
 *   :heading-id="`heading-${liquid.sectionId}`"
 *   :namespace="liquid.sectionId"
 *   :tabs="tabs"
 * >
 *   <template #tabpanel="tab">
 *     <ul>
 *       <li
 *         v-for="(content, index) of tabObject.contentArray"
 *         :key="`component-${liquid.sectionId}-${index}`"
 *         v-text="content.title"
 *       />
 *     </ul>
 *   </template>
 * </tabs-snippet>
 *
 * @param {String} [btnModifiers] - Button modifiers in space separated list
 * @param {Object} [classes] - Optional classes.
 * @param {String} [classes.tab] - Tab class.
 * @param {String} [classes.tabLabel] - Tab button label text class.
 * @param {String} [classes.tabslist] - Tablist class.
 * @param {String} [classes.tabslistContainer] - Tablist container class.
 * @param {String} [classes.tabspanel] - Tabpanel class.
 * @param {Boolean} [content] - Render panel content.
 * @param {Boolean} [dynamic] - Use dynamic named slots, uses tab ID as name.
 * @param {String} headingId - Heading ID for aria-labelledby.
 * @param {Number} [initialActiveIndex] - Default to a tab other than the first
 * one.
 * @param {String} namespace - Unique namespace.
 * @param {Object} parentActiveTab - Allows parent component to set active tab.
 * @param {Array} tabs - Array of tabs.
 * @param {String} tabs[].id - Component-level unique tab ID.
 * @param {String} tabs[].title - Tab trigger title.
 *
 * @emits selectedTab - Emit current tab object.
 *
 */
import Btn from '~global/btn/btn'

export default {
  name: 'TabsSnippet',

  components: {
    Btn,
  },

  props: {
    btnModifiers: {
      type: String,
      default: 'tab',
    },
    classes: {
      type: Object,
      default: () => ({
        tab: '',
        tabslist: '',
        tabslistContainer: '',
        tabspanel: '',
      }),
    },
    content: {
      type: Boolean,
      default: true,
    },
    dynamic: {
      type: Boolean,
      default: false,
    },
    headingId: {
      type: String,
      default: '',
      required: true,
    },
    initialActiveIndex: {
      type: Number,
      default: 0,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    parentActiveTab: {
      type: Object,
      default: () => ({}),
    },
    tabs: {
      type: Array,
      default: () => [],
      required: true,
    },
  },

  emits: { 'selected-tab': (payload) => typeof payload === 'object' },

  data() {
    return {
      activeTab: this.tabs[0],
    }
  },

  watch: {

    /**
     * Watch active tab to emit update.
     * - Use watch so it can be emitted immediately.
     */
    activeTab: {
      handler() {
        this.$emit('selected-tab', this.activeTab)
      },
      immediate: true,
    },

    /**
     * Watch parent active tab to override component active tab.
     */
    parentActiveTab() {
      this.activeTab = this.parentActiveTab
    },
  },

  mounted() {
    this.initialiseTabsSnippet()
  },

  methods: {

    /**
     * Return unique panel ID.
     * @param {Number} index - Tabs index.
     * @returns {String}
     */
    getPanelId(index) {
      return `panel-${index}-${this.namespace}`
    },

    /**
     * Return unique tab ID.
     * @param {Number} index - Tabs index.
     * @returns {String}
     */
    getTabId(index) {
      return `tab-${index}-${this.namespace}`
    },

    /**
     * Return button tabindex.
     * - Set to null if active because tabindex="0" on a button is redundant.
     * @param {String} id - Tab ID.
     * @returns {Number|null}
     */
    getTabTabindex(id) {
      return this.isActiveTab(id)
        ? null
        : -1
    },

    /**
     * Initialise component by setting the active tab to the first tab in the
     * tabs array.
     */
    initialiseTabsSnippet() {
      if (!this.tabs.length) {
        return
      }

      this.activeTab = this.tabs[this.initialActiveIndex]
    },

    /**
     * Return if a given tab is active.
     * @param {String} id - Tab ID.
     * @returns {Boolean}
     */
    isActiveTab(id) {
      return id === this.activeTab.id
    },

    /**
     * Handle keydown events on tablist buttons.
     * @param {Event} $event - Keydown event.
     * @param {Number} index - Index of current tab.
     */
    handleTabKeydown($event, index) {
      let flag = false
      let targetIndex = index

      switch ($event.key) {
        case 'ArrowLeft':
          targetIndex = index - 1 >= 0
            ? index - 1
            : index

          flag = true
          break

        case 'ArrowRight':
          targetIndex = index + 1 < this.tabs.length
            ? index + 1
            : index

          flag = true
          break

        case 'Home':
          targetIndex = 0
          flag = true
          break

        case 'End':
          targetIndex = this.tabs.length - 1
          flag = true
          break

        default:
          break
      }

      if (!flag) {
        return
      }

      $event.stopPropagation()
      $event.preventDefault()

      this.setSelectedTab(targetIndex)
    },

    /**
     * Manually update the selected tab.
     * @param {Number} index - Index of target tab.
     */
    setSelectedTab(index) {
      this.activeTab = this.tabs[index]
      this.setTabFocus(this.activeTab.id)
    },

    /**
     * Set focus of button associated with the given ID.
     * @param {String} id - Tab ID.
     */
    setTabFocus(id) {
      const targetRef = this.$refs[id]

      if (!targetRef || !targetRef[0]) {
        return
      }

      targetRef[0].$el.focus()
    },
  },
}
</script>

<style lang="scss">
@import './tabs-snippet';
</style>
